import { createApp } from "vue";
import App from "./App.vue";
import store from "./store";
import router from "@/router/router";
import { actionTypes, dbName } from "@/store-types";

import { createBootstrap } from "bootstrap-vue-next";
import "bootstrap/dist/css/bootstrap.rtl.min.css";
import "bootstrap-vue-next/dist/bootstrap-vue-next.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@/assets/app-theme.css";
import "@/assets/dark-theme.css";
import "@/assets/print.css";

import DatePicker from "vue3-persian-datetime-picker";
import SectionIntro from "@/components/common/SectionIntro.vue";
import GuideTour from "@/components/common/GuideTour.vue";
import toastrPlugin from "@/plugins/toastr";
import filters from "./filter/filters.js";

import wb from "./registerServiceWorker";
import { reportClientError } from "@/utils/logger";
import { createPersister, clearPersistedState } from "@/utils/persistedState";
import { enforceAppVersion } from "@/utils/versionGuard";
import { initTheme } from "@/utils/theme";
import { initPwaInstall } from "@/utils/pwaInstall";
import { initNetworkWatch } from "@/utils/network";
import { initMonitoring, setMonitoringUser } from "@/utils/monitoring";

// Firebase کاملاً حذف شد (RTDB + FCM). به‌روزرسانی تیکت‌ها با polling از وب‌سرویس،
// و اعلان‌ها از endpointهای داخلی است.

const persist = createPersister(dbName.DB_STATE);

const app = createApp(App);

// router عمداً اینجا use نمی‌شود؛ پس از بازیابی نشست در bootstrap() اضافه می‌شود
// تا نگهبان مسیر با وضعیت درست ورود، نخستین ناوبری را ارزیابی کند.
app.use(store);
app.use(createBootstrap());
app.use(toastrPlugin);

app.component("date-picker", DatePicker);
app.component("section-intro", SectionIntro);
app.component("guide-tour", GuideTour);

// propertyهای سراسری (معادل Vue.prototype در Vue 2)
app.config.globalProperties.$workbox = wb;
app.config.globalProperties.$filters = filters;

// ------------------------------------------------------------------
// راه‌اندازی برنامه — با مدیریت خطا تا هیچ‌وقت صفحه‌ی سفید نمانَد
// ------------------------------------------------------------------

// اسپلش‌اسکرینِ index.html سه انیمیشن infinite دارد (چرخش/تنفس/جاروب گرادیان).
// Vue با mount شدن روی #app آن را پاک می‌کند، ولی اگر bootstrap در یک await
// (مثلاً آفلاین) گیر کند، اسپلش و انیمیشن‌هایش تا ابد CPU/GPU می‌خورند.
// این تابع، mount و پاک‌سازیِ صریح را در هر حالت تضمین می‌کند.
let _mounted = false;
function mountApp() {
    if (!_mounted) {
        try {
            app.mount("#app");
            _mounted = true;
        } catch (e) {
            /* noop */
        }
    }
    try {
        const boot = document.querySelector(".bndbs-boot");
        if (boot && boot.parentNode) boot.parentNode.removeChild(boot);
    } catch (e) {
        /* noop */
    }
}

/** یک Promise را با مهلت‌زمان همراه می‌کند تا هیچ awaitِ راه‌اندازی برای همیشه بلاک نشود. */
function withTimeout(promise, ms) {
    return Promise.race([
        Promise.resolve(promise),
        new Promise((resolve) => setTimeout(resolve, ms)),
    ]);
}

async function bootstrap() {
    // اگر راه‌اندازی به هر دلیلی طول بکشد، حداکثر بعد از ۱۲ ثانیه اپ mount می‌شود
    // (و اسپلش با انیمیشن‌هایش برداشته می‌شود).
    const safety = setTimeout(mountApp, 12000);
    try {
        await bootstrapInner();
    } catch (e) {
        reportClientError({ source: "bootstrap:fatal", error: e });
    } finally {
        clearTimeout(safety);
        mountApp();
    }
}

async function bootstrapInner() {
    initTheme();
    initPwaInstall();
    initNetworkWatch();
    await initMonitoring();

    let appLoadOk = false;
    try {
        await store.dispatch(actionTypes.APP_LOAD);
        appLoadOk = true; // فقط وقتی واقعاً از سرور پاسخ گرفتیم
    } catch (e) {
        reportClientError({ source: "bootstrap:APP_LOAD", error: e });
    }

    // تغییر config.Version → پاک‌سازی کش‌های داده/API؛ تغییر config.PurgeToken →
    // پاک‌سازی کامل همه‌ی کش‌ها + ری‌لود. جزئیات در utils/versionGuard.js.
    if (appLoadOk) {
        const halted = await enforceAppVersion(store.state.config, () =>
            clearPersistedState(dbName.DB_STATE)
        );
        if (halted) return; // ری‌لود در جریان است؛ ادامه نده
    }

    try {
        await store.dispatch(actionTypes.LOAD_LOCATION);
        await store.dispatch(actionTypes.CHECK_TOKEN);
        await store.dispatch(actionTypes.LOAD_USER_DATA);
    } catch (e) {
        reportClientError({ source: "bootstrap:session", error: e });
    }

    if (store.state.signedIn) {
        setMonitoringUser(store.state.user && store.state.user.UserId);
    }

    store.subscribe((_mutation, state) => persist(state));

    installErrorHandlers();

    // اکنون که نشست بازیابی شد، router را اضافه می‌کنیم؛ نگهبانِ داخل
    // router.js نخستین ناوبری را با signedIn درست ارزیابی می‌کند.
    app.use(router);
    await withTimeout(router.isReady(), 8000);

    mountApp();
}

function installErrorHandlers() {
    app.config.errorHandler = (err, instance, info) => {
        reportClientError({ source: "vue", error: err, info, userId: getUserId() });
    };

    window.addEventListener("error", (event) => {
        reportClientError({
            source: "window.error",
            error: event.error || event.message,
            userId: getUserId(),
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        reportClientError({
            source: "unhandledrejection",
            error: event.reason,
            userId: getUserId(),
        });
    });
}

function getUserId() {
    try {
        return store.state.signedIn ? store.state.user.UserId || 0 : 0;
    } catch (e) {
        return 0;
    }
}

bootstrap();
